import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getServiceBySlug } from '../data/services';

const {
  FiArrowLeft, FiCheckCircle, FiArrowRight, FiPhone, FiMail,
  FiCode, FiTrendingUp, FiPenTool, FiSmartphone, FiShoppingCart, FiSettings,
  FiClock, FiTarget, FiUsers, FiZap, FiStar
} = FiIcons;

const iconMap = {
  FiCode, FiTrendingUp, FiPenTool, FiSmartphone, FiShoppingCart, FiSettings
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const ServiceIcon = iconMap[service.icon];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <div className="fixed top-24 left-8 z-40">
        <Link
          to="/"
          className="flex items-center space-x-2 bg-white/90 backdrop-blur-md text-slate-700 px-4 py-2 rounded-full shadow-lg hover:bg-white transition-all duration-300"
        >
          <SafeIcon icon={FiArrowLeft} />
          <span className="font-medium">Back to Home</span>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <div className="mb-6 flex items-center space-x-4">
              <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center">
                <SafeIcon icon={ServiceIcon} className="text-white text-2xl" />
              </div>
              <span className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                Our Services
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {service.title}
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mb-8">
              {service.subtitle}
            </p>
            <p className="text-lg text-slate-200 max-w-4xl mb-8 leading-relaxed">
              {service.overview}
            </p>

            {/* Pricing Preview */}
            <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl p-6 max-w-md mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <SafeIcon icon={FiStar} className="text-orange-400" />
                <span className="text-lg font-semibold">Starting Price</span>
              </div>
              <div className="text-3xl font-bold text-orange-400 mb-2">
                {service.pricing.startingPrice}
              </div>
              <p className="text-slate-300 text-sm">
                Custom quotes available for all project sizes
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="group bg-orange-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-orange-600 transition-all duration-300 inline-flex items-center justify-center space-x-3"
              >
                <span>Get Started Today</span>
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              {/* <a
                href="tel:+447769061291"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300 inline-flex items-center justify-center space-x-3"
              >
                <SafeIcon icon={FiPhone} />
                <span>Call: 07769061291</span>
              </a> */}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Benefits Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                <SafeIcon icon={FiTarget} className="inline mr-3 text-orange-500" />
                Why Choose Our {service.title} Services?
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {service.description}
              </p>
              <div className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700 leading-relaxed">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Process Steps */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-8 rounded-2xl"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-6">
                <SafeIcon icon={FiZap} className="inline mr-3 text-orange-500" />
                Our Process
              </h3>
              <div className="space-y-6">
                {service.process.map((step, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {step.step}
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">{step.title}</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {service.title} Pricing Packages
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Choose the perfect package for your business needs. All packages include our quality guarantee and ongoing support.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {service.pricing.packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200"
              >
                <div className="text-center mb-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{pkg.name}</h3>
                  <div className="text-2xl font-bold text-orange-600 mb-4">{pkg.price}</div>
                </div>
                <div className="space-y-4 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-slate-700 text-sm leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/contact"
                  className="w-full bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors duration-300 inline-flex items-center justify-center space-x-2"
                >
                  <span>Get Quote</span>
                  <SafeIcon icon={FiArrowRight} />
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <div className="bg-white rounded-2xl p-8 max-w-2xl mx-auto shadow-lg">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Need a Custom Solution?</h3>
              <p className="text-slate-600 mb-6">
                Every business is unique. Let's discuss your specific requirements and create a tailored solution that fits your needs and budget.
              </p>
              <Link
                to="/contact"
                className="bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-colors duration-300 inline-flex items-center space-x-2"
              >
                <span>Discuss Custom Project</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Technologies & Tools We Use
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We leverage the latest technologies and industry best practices to deliver exceptional results.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {service.technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-slate-50 p-4 rounded-lg text-center border border-slate-200 hover:border-orange-200 hover:shadow-md transition-all duration-300"
              >
                <span className="text-slate-700 font-medium">{tech}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      {service.projects.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Recent {service.title} Projects
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                See how we've helped businesses succeed with our {service.title.toLowerCase()} services.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {service.projects.map((project, index) => (
                <motion.div
                  key={project.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={`/projects/${project.slug}`}
                    className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                          {project.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors duration-300">
                        {project.title}
                      </h3>
                      <div className="mb-4">
                        <div className="text-sm font-semibold text-slate-900 mb-1">Result:</div>
                        <div className="text-orange-600 font-medium">{project.result}</div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.tech.map((tech, techIndex) => (
                          <span
                            key={techIndex}
                            className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center space-x-2 text-orange-600 group-hover:text-orange-700 font-medium transition-colors duration-300">
                        <span>View Project Details</span>
                        <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/projects"
                className="inline-flex items-center space-x-2 text-orange-600 hover:text-orange-700 font-medium transition-colors duration-300"
              >
                <span>View All Projects</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Let's discuss your {service.title.toLowerCase()} needs and create a solution that drives real results for your business.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-slate-800 p-6 rounded-xl">
                <SafeIcon icon={FiPhone} className="text-orange-400 text-2xl mb-3 mx-auto" />
                <h3 className="font-semibold mb-2">Call Us Today</h3>
                <p className="text-slate-300 text-sm mb-3">Speak directly with our team</p>
                {/* <a
                  href="tel:+447769061291"
                  className="text-orange-400 hover:text-orange-300 font-medium"
                >
                  07769061291
                </a> */}
              </div>
              <div className="bg-slate-800 p-6 rounded-xl">
                <SafeIcon icon={FiMail} className="text-orange-400 text-2xl mb-3 mx-auto" />
                <h3 className="font-semibold mb-2">Email Enquiry</h3>
                <p className="text-slate-300 text-sm mb-3">Get a detailed proposal</p>
                <a
                  href="mailto:hello@wedesignedit.co.uk"
                  className="text-orange-400 hover:text-orange-300 font-medium"
                >
                  hello@wedesignedit.co.uk
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-orange-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-orange-600 transition-all duration-300 inline-flex items-center justify-center space-x-3"
              >
                <span>Get Free Quote</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
              <Link
                to="/projects"
                className="border border-slate-600 text-slate-300 px-8 py-4 rounded-full font-semibold hover:bg-slate-800 transition-colors duration-300"
              >
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;